import { api } from '../lib/api.js';

export type SupportCategory = { id: string; name: string };
export type SupportLogRow = { id: string; categoryName: string; minutes: number; projectNumber: string | null; note: string | null };
export type WorkerDay = {
  employeeName: string; shiftMinutes: number; productionMin: number; supportMin: number; reportedMin: number; gapMin: number;
  logs: SupportLogRow[];
};
export type DailyRow = {
  employeeId: string; employeeNumber: string; name: string;
  attendedMin: number; productionMin: number; supportMin: number; reportedMin: number;
  /** Signed: negative means MORE was reported than the shift is long. */
  gapMin: number;
  /** Minutes beyond the shift. Zero when there is no excess. */
  overReportedMin: number;
  covered: boolean;
  /** מה העובד ענה על הזמן החסר בסיום היום (סקר עמדה #11). */
  gapReports?: GapReport[];
};
export type DailySummary = { shiftMinutes: number; rows: DailyRow[] };
/** „דיווח עובד על זמן חסר" — מה העובד ענה בסיום היום. */
export type GapReport = { id: string; minutes: number; label: string | null; note: string | null };
export type GapKind = 'LAST_PART' | 'CLEANING' | 'WAITING' | 'OTHER';
/** נוכחות היום מול מה שנרשם — לשאלה בסיום יום עבודה. */
export type EndDayGap = {
  presentMin: number | null; recordedMin: number; gapMin: number; ask: boolean;
  last: { partId: string; partNumber: string; partName: string | null; projectNumber: string } | null;
};
export type ResolvedPart = { partId: string; partNumber: string; name: string | null; projectId: string; projectNumber: string };
export type ProjectPart = { partNumber: string; name: string | null };
export type EngineerLogRow = { id: string; date: string; categoryName: string | null; minutes: number; projectNumber: string | null; note: string | null };
export type ProjectLaborRow = { id: string; who: string; isEngineer: boolean; categoryName: string | null; partNumber: string | null; minutes: number; date: string; source: string; note: string | null };
export type ProjectLabor = { totalMinutes: number; rows: ProjectLaborRow[] };

export const supportApi = {
  categories: (domain: 'PRODUCTION' | 'ENGINEERING' = 'PRODUCTION') => api<SupportCategory[]>(`/api/support/categories?domain=${domain}`),
  addCategory: (name: string, domain: 'PRODUCTION' | 'ENGINEERING' = 'PRODUCTION') => api<{ id: string }>('/api/support/categories', { method: 'POST', body: { name, domain } }),
  removeCategory: (id: string) => api<{ ok: true }>(`/api/support/categories/${id}`, { method: 'DELETE' }),

  projectLabor: (projectId: string) => api<ProjectLabor>(`/api/support/project-labor?projectId=${projectId}`),
  resolvePart: (payload: string) => api<ResolvedPart>(`/api/support/resolve-part?payload=${encodeURIComponent(payload)}`),
  projectParts: (projectId: string) => api<ProjectPart[]>(`/api/support/project-parts?projectId=${projectId}`),
  qrPngUrl: (payload: string): string => `/api/support/qr.png?payload=${encodeURIComponent(payload)}`,
  engineerLog: (body: { categoryId?: string | null; minutes: number; projectId?: string | null; note?: string; date?: string; forUserId?: string }) =>
    api<{ id: string }>('/api/support/engineer/log', { method: 'POST', body }),
  engineerRecent: () => api<EngineerLogRow[]>('/api/support/engineer/recent'),
  engineerTargets: () => api<{ id: string; name: string }[]>('/api/support/engineer/targets'),

  stationLog: (body: { employeeNumber: string; categoryId: string; minutes: number; projectNumber?: string; note?: string; date?: string }) =>
    api<{ id: string }>('/api/support/station/log', { method: 'POST', body }),
  stationDay: (employeeNumber: string, date?: string) =>
    api<WorkerDay>(`/api/support/station/day?employeeNumber=${encodeURIComponent(employeeNumber)}${date ? `&date=${date}` : ''}`),
  endDayGap: (employeeNumber: string) =>
    api<EndDayGap>(`/api/support/station/end-day-gap?employeeNumber=${encodeURIComponent(employeeNumber)}`),
  reportGap: (body: { employeeNumber: string; kind: GapKind; note?: string }) =>
    api<{ id: string; minutes: number }>('/api/support/station/gap-report', { method: 'POST', body }),

  summary: (date: string) => api<DailySummary>(`/api/support/summary?date=${date}`),
  managerLog: (body: { employeeId?: string; employeeNumber?: string; categoryId?: string | null; minutes: number; projectId?: string | null; partId?: string | null; note?: string; date?: string }) =>
    api<{ id: string }>('/api/support/log', { method: 'POST', body }),
  // The reason is mandatory server-side (it is an audited change to reported
  // hours), so it is mandatory here — a caller cannot forget it and discover
  // the 400 at runtime.
  removeLog: (id: string, reason: string) =>
    api<{ ok: true }>(`/api/support/logs/${id}`, { method: 'DELETE', body: { reason } }),
};

/**
 * משך כזמן קריא: `1:30`, או `45 דק׳` כשאין שעה שלמה.
 *
 * `minutesLabel` הוא פרמטר ולא קבוע, כי הפונקציה הזאת נקראת גם ממסכי
 * המשרד (עברית תמיד) וגם מעמדת העובד, שנקראת גם ברוסית. עד 09/09/2026
 * היא הדביקה „דק׳“ בעברית, וזה הופיע כ„Производство 0 דק׳“ — חצי רוסית
 * וחצי עברית באותה שורה.
 *
 * פונקציית עזר שמחזירה טקסט לאדם היא מקום ששום `t()` לא מגיע אליו, ולכן
 * היא נראית תמימה ואינה.
 */
export const fmtHm = (min: number, minutesLabel = 'דק׳'): string => {
  const h = Math.floor(min / 60); const m = Math.round(min % 60);
  // `h:mm` חסר מילים ממילא, ולכן זהה בכל שפה.
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}` : `${m} ${minutesLabel}`;
};
